import React from 'react';
import { LayoutGrid, Maximize, Star } from 'lucide-react';
import { getSizeClass, ICON_MAP } from '../../config/constants';

export const Dashboard = ({
    isDarkMode,
    homeZoom,
    handleHomeZoomChange,
    displayModules,
    userFavorites,
    handleToggleFavorite,
    handleCycleCardSize,
    handleOpenModule,
    setDraggedItem,
    handleDrop
}) => {
    return (
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-8 animate-fade-in">
            <div className="max-w-7xl mx-auto flex flex-col gap-6">
                
                {/* HEADER E CONTROLES DA HOME */}
                <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-2xl shadow-sm border ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div>
                        <h1 className={`text-xl font-black flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                            <LayoutGrid className="text-blue-500" /> Início (Hub)
                        </h1>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Selecione um módulo para começar.</p>
                    </div>

                    {/* SELETOR DE ZOOM */}
                    <div className={`flex items-center gap-2 rounded-full px-4 py-2 shadow-sm border transition-colors ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-gray-200'}`}>
                        <div className="bg-blue-600 rounded-full p-1.5 flex items-center justify-center shrink-0" title="Diminuir">
                            <LayoutGrid size={12} className="text-white" />
                        </div>
                        <input 
                            type="range" min="1" max="10" 
                            value={homeZoom} 
                            onChange={handleHomeZoomChange}
                            className={`w-24 md:w-32 h-1.5 rounded-lg appearance-none cursor-pointer accent-blue-600 transition-colors ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}
                            title={`Zoom: ${homeZoom}`}
                        />
                        <Maximize size={12} className={isDarkMode ? 'text-slate-300' : 'text-slate-500'} title="Aumentar" />
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-start">
                    
                    {/* GRADE DINÂMICA COM ZOOM (HOME) */}
                    <div className="flex-1 w-full grid gap-4 grid-flow-dense" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${80 + (homeZoom * 15)}px, 1fr))`, gridAutoRows: `${80 + (homeZoom * 15)}px` }}>
                        {displayModules.filter(m => m.moduleType !== 'externo' && !m.parentId).map(mod => {
                            const sizeClass = getSizeClass(mod.size);
                            return (
                                <div 
                                    key={mod.id}
                                    onClick={() => handleOpenModule(mod)}
                                    draggable="true"
                                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', mod.id); setDraggedItem(mod); }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => handleDrop(e, mod, false)}
                                    className={`${sizeClass} rounded-3xl shadow-lg cursor-pointer transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl flex flex-col items-center justify-center gap-2 md:gap-3 bg-gradient-to-br ${mod.color} text-white relative overflow-hidden group border border-white/10`}
                                >
                                    <button 
                                        onClick={(e) => handleToggleFavorite(e, mod)}
                                        className="absolute top-4 left-4 p-2 bg-black/10 hover:bg-black/30 rounded-full transition-all backdrop-blur-sm z-10"
                                        title="Favoritar Módulo"
                                    >
                                        <Star size={16} className={`drop-shadow-md transition-colors ${userFavorites.some(f => f.moduleId === mod.id) ? 'text-yellow-400 fill-yellow-400' : 'text-white'}`} />
                                    </button>

                                    <button 
                                        onClick={(e) => handleCycleCardSize(e, mod, false)}
                                        className="absolute top-4 right-4 p-2 bg-black/10 hover:bg-black/30 rounded-full transition-all backdrop-blur-sm z-10 opacity-0 group-hover:opacity-100"
                                        title="Alterar Formato do Card"
                                    >
                                        <Maximize size={16} className="text-white drop-shadow-md" />
                                    </button>

                                    {mod.customIconUrl ? (
                                        <img src={mod.customIconUrl} alt="icon" className="w-10 h-10 object-contain drop-shadow-md group-hover:scale-110 transition-transform" />
                                    ) : (
                                        React.createElement(mod.icon || ICON_MAP[mod.iconName] || LayoutGrid, { 
                                            size: sizeClass.includes('row-span-2') ? 48 : 36, 
                                            className: "drop-shadow-md group-hover:scale-110 transition-transform" 
                                        })
                                    )}
                                    <span className={`font-bold tracking-wide drop-shadow-md text-center px-2 ${sizeClass.includes('col-span-2') ? 'text-sm' : 'text-[11px] md:text-xs'}`}>{mod.title}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* QUADRO DIREITO: ACESSOS EXTERNOS */}
                    <div className={`w-full md:w-64 shrink-0 rounded-2xl shadow-sm border p-4 flex flex-col gap-3 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <h3 className={`text-sm font-black uppercase tracking-wider mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Acessos Externos</h3>
                        
                        {displayModules.filter(m => m.moduleType === 'externo').map(mod => (
                            <div key={mod.id} className={`group relative flex items-center justify-between p-2 rounded-xl border transition-all cursor-pointer hover:-translate-y-0.5 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-500' : 'bg-slate-50 border-gray-200 hover:border-gray-400'}`} onClick={() => handleOpenModule(mod)}>
                                <div className="flex items-center gap-3 overflow-hidden">
                                    {mod.customIconUrl ? (
                                        <img src={mod.customIconUrl} alt="icon" className="w-8 h-8 rounded-lg object-cover shrink-0 shadow-sm border border-slate-200 dark:border-slate-700 bg-white" referrerPolicy="no-referrer" />
                                    ) : (
                                        <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center bg-gradient-to-br ${mod.color || 'from-slate-500 to-slate-600'} text-white shadow-sm`}>
                                            {React.createElement(mod.icon || LayoutGrid, { size: 14 })}
                                        </div>
                                    )}
                                    <span className="font-bold text-xs truncate pr-2">{mod.title}</span>
                                </div>
                                <button onClick={(e) => handleToggleFavorite(e, mod)} className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors z-10 shrink-0" title="Favoritar">
                                    <Star size={14} className={`${userFavorites.some(f => f.moduleId === mod.id) ? 'text-yellow-400 fill-yellow-400' : (isDarkMode ? 'text-slate-500' : 'text-gray-400')}`} />
                                </button>
                            </div>
                        ))}
                        {displayModules.filter(m => m.moduleType === 'externo').length === 0 && <p className="text-xs opacity-50 text-center py-4">Nenhum acesso cadastrado.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
